import {useEffect, useState} from 'react'
import {ActionMenu, Text} from '@primer/react-brand/esm'
import {findAllByContentType, type GenericGroup} from '../../../../../brand/lib/types/contentful'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {announce} from '@github-ui/aria-live'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {t} from '@github-ui/swp-core/lib/utils/localization'
import {CopyIcon} from '@primer/octicons-react'

import styles from './CommandActionMenu.module.css'

type CopyStatus = 'copied' | 'failed'

const getInstallAnalytics = (action: string, location: string) =>
  getAnalyticsEvent({action, tag: 'button', context: 'CTAs', location})

type Props = {
  component: GenericGroup
  location: string
  fullWidth?: boolean
}

export function CommandActionMenu({component, fullWidth, location}: Props) {
  const allItems = findAllByContentType(component.fields.content, 'genericContent')
  // In Contentful, there is no dedicated button label field, so the first item's heading is used
  const defaultButtonLabel = allItems[0]?.fields.heading ?? 'Install in CLI'
  const items = allItems.flatMap(item =>
    item.fields.subheading ? [{item, textToCopy: documentToPlainTextString(item.fields.subheading)}] : [],
  )

  const [copyStatus, setCopyStatus] = useState<CopyStatus | undefined>()

  useEffect(() => {
    if (!copyStatus) return
    const timeout = setTimeout(() => setCopyStatus(undefined), 2000)
    return () => clearTimeout(timeout)
  }, [copyStatus])

  async function handleSelect(value: string) {
    try {
      await navigator.clipboard.writeText(value)
      setCopyStatus('copied')
      announce(t('Copied to clipboard'))
    } catch {
      setCopyStatus('failed')
      announce(t('Could not copy to clipboard'))
    }
  }

  if (items.length === 0) return null

  const buttonLabels: Record<CopyStatus, string> = {copied: t('Copied!'), failed: t('Copy failed')}
  const buttonLabel = copyStatus ? buttonLabels[copyStatus] : defaultButtonLabel

  return (
    <ActionMenu onSelect={value => void handleSelect(value)}>
      <ActionMenu.Button className={fullWidth ? styles.fullWidth : undefined}>{buttonLabel}</ActionMenu.Button>
      <ActionMenu.Overlay aria-label="Install options">
        {items.map(({item, textToCopy}) => {
          return (
            <ActionMenu.Item
              key={item.sys.id}
              className={styles.item}
              value={textToCopy}
              {...getInstallAnalytics(item.fields.heading ?? textToCopy, location)}
            >
              <Text className={styles.heading} variant="muted">
                {item.fields.heading}
              </Text>

              <Text font="monospace">{textToCopy}</Text>

              <CopyIcon className={styles.icon} aria-hidden />
            </ActionMenu.Item>
          )
        })}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
